// Shared anatomy types and trait-driven detail helpers used by every body plan
// (anatomy.ts = classic forms, anatomyForms.ts = new animals + fantasy, anatomySynth.ts = synths).
//
// Coordinate system: y-down, origin at the creature's feet centre, H = body height (already
// multiplied by genome.sizeScale). Mirrored limbs use `side` = -1 (left) / +1 (right). Limbs hanging
// down are drawn along +y in local space for the LEFT side; the animator sets rotation =
// -side * angle, so a positive angle always swings a limb outward/up and negative inward.
import { Container, Graphics } from "pixi.js";
import { INK, mix } from "../palette";
import type { CreaturePose } from "./pose";
import type { CreatureGenome } from "./genome";
import { blob, blot, chord, disc, noodle, oval } from "./draw";

export interface Colors {
  base: number;
  accent: number;
  belly: number;
  /** Light tint of base (shine, glow). */
  light: number;
  /** Extra Okabe-Ito accents (0–3, hue-shifted with the creature). */
  palette: number[];
}

export type EyeStyle = "normal" | "knob" | "screen";
export type MouthStyle = "normal" | "beak" | "keys" | "steps";

export interface Geo {
  /** Main body ellipse (markings / belly are fitted into it). */
  cy: number;
  rx: number;
  ry: number;
  /** Spin pivot and glow centre (y). */
  centerY: number;
  /** Silhouette half width (shadow, spotlight). */
  halfW: number;
  /** Top of the head (hat anchor) and usable head width. */
  headTopY: number;
  headW: number;
  /** Top of the whole silhouette, incl. stalks/antennae (Zzz anchor). */
  topY: number;
  eyeY: number;
  eyeDx: number;
  eyeR: number;
  mouthY: number;
  mouthW: number;
  neckY: number;
  neckHalfW: number;
  /** Floats above the ground (Glimmer). */
  floats: boolean;
  /** Optional overrides (default "normal"). */
  eyeStyle?: EyeStyle;
  mouthStyle?: MouthStyle;
  /** White keys in a "keys" mouth / step keys in a "steps" mouth (multiple of 4). */
  mouthKeys?: number;
  /** Max depth (× H) an open "steps" mouth drops below its key row (default 0.1). */
  mouthDepth?: number;
  /** Light outlined colour for face lines on dark bodies (happy eyes); default plain INK lines. */
  faceInk?: number;
  /** Multiplier on the pose squash (heavy kick machines thump harder; default 1). */
  squashScale?: number;
  /** Beak length multiplier for a "beak" mouth. */
  beakScale?: number;
  /** Fill of the screen-eye lids (LCD colour). */
  screenColor?: number;
  /** Always-on halo strength (glowing forms), added to the trait aura. */
  selfGlow?: number;
  /** Force fangs regardless of genome.teeth. */
  fangs?: boolean;
}

export type LimbRole = "arm" | "leg" | "claw" | "flipper" | "wing" | "tail" | "antenna" | "tendril" | "pod";

export interface Limb {
  node: Container;
  role: LimbRole;
  side: -1 | 0 | 1;
  index: number;
  /** Rest angle (see header comment). */
  rest: number;
  /** Snapper only: the moving upper jaw of the claw. */
  jaw?: Graphics;
}

export interface Anatomy {
  geo: Geo;
  back: Container;
  body: Graphics;
  front: Container;
  limbs: Limb[];
  /** Redraw every colored part (called on hue change). Body is drawn via drawBody. */
  drawers: Array<(c: Colors) => void>;
  /** Draw the body; `ripple` 0..1 and `phase` (radians) are used by Wobbler forms. */
  drawBody: (c: Colors, ripple: number, phase: number) => void;
  /** Optional per-frame animation of form-specific parts (transforms / visibility only). */
  tick?: (p: CreaturePose, t: number, hitAmt: number) => void;
}

export const sides = [-1, 1] as const;
export const TAU = Math.PI * 2;

export function newAnatomyParts() {
  return {
    back: new Container(),
    front: new Container(),
    body: new Graphics(),
    limbs: [] as Limb[],
    drawers: [] as Anatomy["drawers"],
  };
}

/** Palette colour i (cycling), or `fallback` when the creature has a single-colour palette. */
export function pal(c: Colors, i: number, fallback = c.accent): number {
  return c.palette.length ? c.palette[i % c.palette.length] : fallback;
}

export function eyeLayout(gn: CreatureGenome, H: number, rx: number, eyeY: number, eyeScale = 1) {
  const eyeR = gn.eyeSize * H * eyeScale;
  const eyeDx = Math.max(gn.eyeSpacing * rx, eyeR * 1.18);
  return { eyeR, eyeDx, eyeY: eyeY + gn.eyeHeight * H * 0.6 };
}

const ow0 = (H: number) => H * 0.02;

export function drawMarkings(
  g: Graphics,
  gn: CreatureGenome,
  cy: number,
  rx: number,
  ry: number,
  H: number,
  c: Colors | number,
  alpha = 1,
): void {
  const m = gn.markings;
  const col = (i: number) => (typeof c === "number" ? c : m.multi ? pal(c, i) : c.accent);
  if (m.kind === "spots") {
    for (let i = 0; i < m.spots.length; i++) {
      const s = m.spots[i];
      // Spots near the middle would cover the face: push them up onto the crown.
      const sy = Math.abs(s.x) < 0.55 ? -Math.max(Math.abs(s.y), 0.55) : s.y;
      const x = s.x * rx * 0.85,
        y = cy + sy * ry * 0.85;
      const room = chord(rx, ry, y - cy) - Math.abs(x);
      const r = Math.min(s.r * H, Math.max(room - ow0(H), 0));
      if (r > 1) g.circle(x, y, r).fill({ color: col(i), alpha });
    }
  } else if (m.kind === "patch") {
    const s = m.spots[0];
    const x = s.x * rx * 0.55,
      y = cy + s.y * ry * 0.6;
    const room = chord(rx, ry, y - cy) - Math.abs(x);
    const r = Math.min(s.r * H, Math.max(room, 0) * 0.9);
    if (r > 1) g.ellipse(x, y, r * 1.2, r).fill({ color: col(0), alpha: alpha * 0.9 });
  } else if (m.kind === "stripes") {
    const w = ry * 0.1;
    for (let i = 0; i < m.stripes; i++) {
      const dy = -ry * 0.82 + i * ry * 0.17;
      const hw = chord(rx, ry, dy) * 0.9 - w * 0.5;
      if (hw <= w) continue;
      const tilt = m.stripeTilt * 0.25 * hw;
      g.moveTo(-hw, cy + dy - tilt)
        .lineTo(hw, cy + dy + tilt)
        .stroke({ width: w, color: col(i), alpha, cap: "round" });
    }
  }
}

export function drawBlush(g: Graphics, gn: CreatureGenome, eyeY: number, eyeDx: number, eyeR: number, cx = 0): void {
  if (!gn.blush) return;
  const dx = gn.eyeCount === 1 ? eyeR * 1.5 : eyeDx + eyeR * 0.55;
  for (const s of sides) blot(g, cx + s * dx, eyeY + eyeR * 1.35, eyeR * 0.5, eyeR * 0.3, gn.cheekColor, 0.6);
}

/** Horns / crest drawn at a head-top anchor. Call BEFORE the body fill so the body covers the base. */
export function drawHorns(
  g: Graphics,
  gn: CreatureGenome,
  topY: number,
  spread: number,
  H: number,
  c: Colors,
  ow: number,
): void {
  const col = mix(c.belly, 0xffffff, 0.3);
  const k = 0.8 + gn.traits.spikiness * 0.5;
  switch (gn.horns) {
    case "none":
      return;
    case "nubs":
      for (const s of sides) disc(g, s * spread, topY + H * 0.02, H * 0.055, col, ow);
      return;
    case "spike":
      for (const s of sides) {
        const x = s * spread;
        blob(
          g,
          [x - H * 0.06, topY + H * 0.05, x + s * H * 0.03 * k, topY - H * 0.15 * k, x + H * 0.06, topY + H * 0.05],
          col,
          ow,
          false,
        );
      }
      return;
    case "curl":
      for (const s of sides) {
        const x = s * spread;
        noodle(
          g,
          [
            x,
            topY + H * 0.04,
            x + s * H * 0.04,
            topY - H * 0.1,
            x + s * H * 0.13,
            topY - H * 0.1,
            x + s * H * 0.12,
            topY - H * 0.03,
          ],
          H * 0.05,
          col,
          ow,
          true,
        );
      }
      return;
  }
}

export function drawEars(
  g: Graphics,
  gn: CreatureGenome,
  topY: number,
  spread: number,
  H: number,
  c: Colors,
  ow: number,
): void {
  const sp = gn.span;
  switch (gn.ears) {
    case "none":
      return;
    case "round":
      for (const s of sides) {
        disc(g, s * spread * sp, topY + H * 0.03, H * 0.09 * sp, c.base, ow);
        blot(
          g,
          s * spread * sp,
          topY + H * 0.04,
          H * 0.045 * sp,
          H * 0.045 * sp,
          gn.palette.length && gn.markings.multi ? pal(c, 1) : c.belly,
        );
      }
      return;
    case "pointy":
      for (const s of sides) {
        const x = s * spread * (0.9 + 0.1 * sp);
        blob(
          g,
          [
            x - H * 0.09,
            topY + H * 0.08,
            x + s * H * 0.08 * sp,
            topY - H * 0.14 * (0.7 + 0.4 * gn.traits.spikiness),
            x + H * 0.09,
            topY + H * 0.06,
          ],
          c.base,
          ow,
          false,
        );
      }
      return;
    case "fin":
      for (const s of sides) {
        const x = s * spread;
        blob(
          g,
          [
            x,
            topY - H * 0.05,
            x + s * H * 0.16 * sp,
            topY - H * 0.1,
            x + s * H * 0.12 * sp,
            topY + H * 0.04,
            x,
            topY + H * 0.06,
          ],
          c.accent,
          ow,
          true,
        );
      }
      return;
  }
}

/** A limb: Graphics inside a pivot container. */
export function limb(
  parent: Container,
  x: number,
  y: number,
  role: LimbRole,
  side: -1 | 0 | 1,
  index: number,
  rest: number,
): { l: Limb; g: Graphics } {
  const node = new Container();
  node.position.set(x, y);
  const g = new Graphics();
  node.addChild(g);
  parent.addChild(node);
  return { l: { node, role, side, index, rest }, g };
}

export function antennae(
  back: Container,
  limbs: Limb[],
  drawers: Anatomy["drawers"],
  gn: CreatureGenome,
  baseY: number,
  spread: number,
  len0: number,
  H: number,
  ow: number,
  glowTips: boolean,
  cx = 0,
  count = gn.antennae,
): void {
  const n = count;
  const len = len0 * (0.75 + 0.35 * gn.tailLength);
  for (let i = 0; i < n; i++) {
    const t = n === 1 ? 0 : (i / (n - 1)) * 2 - 1; // -1..1
    const { l, g } = limb(back, cx + t * spread, baseY, "antenna", 0, i, t * 0.35);
    limbs.push(l);
    drawers.push((c) => {
      g.clear();
      const tipX = t * len * 0.35;
      noodle(g, [0, 0, tipX * 0.3, -len * 0.55, tipX, -len], H * 0.03, c.accent, ow * 0.8, true);
      const tip = glowTips ? mix(c.light, 0xfff3a8, 0.6) : gn.markings.multi ? pal(c, i) : c.accent;
      disc(g, tipX, -len, H * (glowTips ? 0.055 : 0.045), tip, ow * 0.8);
    });
  }
}

/**
 * The main body ellipse. Fuzzy creatures (fuzz ≥ 0.25) get a scalloped, fluffy outline whose
 * bumps grow with fuzz; the bottom quarter stays smooth so feet sit cleanly.
 */
export function bodyOval(
  g: Graphics,
  gn: CreatureGenome,
  x: number,
  y: number,
  rx: number,
  ry: number,
  fill: number,
  ow: number,
): Graphics {
  if (gn.fuzz < 0.25) return g.ellipse(x, y, rx, ry).fill({ color: fill }).stroke({ width: ow, color: INK });
  traceFur(g, x, y, rx, ry, gn.fuzz);
  return g.fill({ color: fill }).stroke({ width: ow, color: INK, join: "round" });
}

/** Trace a scalloped ellipse (closed path, not filled). */
export function traceFur(g: Graphics, x: number, y: number, rx: number, ry: number, fuzz: number): Graphics {
  const n = Math.round(12 + fuzz * 10);
  const amp = 0.05 + 0.09 * fuzz;
  // y-down: start lower-right (0.32π), sweep up over the top (−π/2) to lower-left (−1.32π).
  const a0 = Math.PI * 0.32;
  const span = -(TAU - Math.PI * 0.36);
  g.moveTo(x + Math.cos(a0) * rx, y + Math.sin(a0) * ry);
  const k = 1 + amp * 2.2;
  for (let i = 0; i < n; i++) {
    const am = a0 + ((i + 0.5) / n) * span,
      ae = a0 + ((i + 1) / n) * span;
    g.quadraticCurveTo(
      x + Math.cos(am) * rx * k,
      y + Math.sin(am) * ry * k,
      x + Math.cos(ae) * rx,
      y + Math.sin(ae) * ry,
    );
  }
  // Smooth bottom arc back to the start.
  for (let i = 1; i <= 6; i++) {
    const a = Math.PI * 0.68 - (i / 6) * Math.PI * 0.36;
    g.lineTo(x + Math.cos(a) * rx, y + Math.sin(a) * ry);
  }
  return g.closePath();
}

/** Spikes along the top arc of an ellipse (spikiness). Call BEFORE the body fill. */
export function drawBackSpikes(
  g: Graphics,
  gn: CreatureGenome,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  H: number,
  c: Colors,
  ow: number,
  from = -0.85,
  to = -0.15,
): void {
  const n = gn.spikes;
  if (n <= 0) return;
  const len = H * (0.07 + 0.06 * gn.traits.spikiness);
  const half = H * 0.045;
  for (let i = 0; i < n; i++) {
    const t = n === 1 ? 0.5 : i / (n - 1);
    const a = Math.PI * (from + (to - from) * t);
    const px = cx + Math.cos(a) * rx * 0.96,
      py = cy + Math.sin(a) * ry * 0.96;
    const nx = Math.cos(a) * ry,
      ny = Math.sin(a) * rx; // outward normal (unnormalised)
    const nl = Math.hypot(nx, ny) || 1;
    const ux = nx / nl,
      uy = ny / nl;
    const col = gn.markings.multi ? pal(c, i) : mix(c.accent, 0xffffff, 0.15);
    blob(
      g,
      [
        px - uy * half - ux * half,
        py + ux * half - uy * half,
        px + ux * len,
        py + uy * len,
        px + uy * half - ux * half,
        py - ux * half - uy * half,
      ],
      col,
      ow * 0.85,
      false,
    );
  }
}

/** Punk mohawk (very spiky + fuzzy). Call BEFORE the body fill. */
export function drawMohawk(
  g: Graphics,
  gn: CreatureGenome,
  cx: number,
  topY: number,
  H: number,
  c: Colors,
  ow: number,
): void {
  if (!gn.mohawk) return;
  const n = 5;
  for (let i = 0; i < n; i++) {
    const t = i / (n - 1) - 0.5;
    const x = cx + t * H * 0.3,
      h = H * (0.2 - Math.abs(t) * 0.12);
    blob(
      g,
      [x - H * 0.05, topY + H * 0.06, x + t * H * 0.08, topY - h, x + H * 0.05, topY + H * 0.06],
      pal(c, i, c.accent),
      ow,
      false,
    );
  }
}

/** Fur tufts on a head top for non-elliptic bodies (fuzz). Call BEFORE the body fill. */
export function drawTufts(
  g: Graphics,
  gn: CreatureGenome,
  cx: number,
  topY: number,
  w: number,
  H: number,
  color: number,
  ow: number,
): void {
  if (gn.fuzz < 0.25) return;
  const n = 2 + Math.round(gn.fuzz * 3);
  for (let i = 0; i < n; i++) {
    const t = n === 1 ? 0 : i / (n - 1) - 0.5;
    const x = cx + t * w;
    noodle(g, [x, topY + H * 0.05, x + t * H * 0.1, topY - H * (0.06 + gn.fuzz * 0.06)], H * 0.05, color, ow, false);
  }
}

/** Small keyboard strip (white keys with black keys), top-left at (x, y). */
export function drawKeys(
  g: Graphics,
  x: number,
  y: number,
  w: number,
  h: number,
  whites: number,
  ow: number,
  lit = -1,
  litColor = 0xf0e442,
): void {
  const kw = w / whites;
  g.roundRect(x, y, w, h, Math.min(h * 0.25, kw * 0.4))
    .fill({ color: 0xfaf7ee })
    .stroke({ width: ow, color: INK });
  for (let i = 0; i < whites; i++) {
    if (i === lit) g.rect(x + i * kw + ow * 0.4, y + ow * 0.4, kw - ow * 0.8, h - ow * 0.8).fill({ color: litColor });
    if (i > 0)
      g.moveTo(x + i * kw, y)
        .lineTo(x + i * kw, y + h)
        .stroke({ width: Math.max(0.6, ow * 0.45), color: INK });
  }
  for (let i = 0; i < whites - 1; i++) {
    const m = i % 7;
    if (m === 2 || m === 6) continue; // no black key between E-F and B-C
    g.roundRect(x + (i + 1) * kw - kw * 0.3, y, kw * 0.6, h * 0.6, kw * 0.12).fill({ color: INK });
  }
}

/** Drum-machine step keys in four colour groups (Okabe-Ito vermillion, orange, yellow + cream). */
export const STEP_COLORS = [0xd55e00, 0xe69f00, 0xf0e442, 0xf4efe1] as const;

/**
 * Geometry of a row of `n` step keys spanning width `w`, centred on x = 0 with its vertical centre
 * on y = 0 (shared by the "steps" mouth and the lit-key overlay so they line up exactly).
 */
export function stepKeyLayout(w: number, n: number, H: number) {
  const pitch = w / n;
  const gap = Math.max(1, pitch * 0.14);
  const kw = pitch - gap;
  const kh = Math.min(Math.max(H * 0.07, kw * 1.05), H * 0.11);
  const keyX = (i: number) => -w / 2 + i * pitch + gap / 2;
  const group = (i: number) => Math.min(3, Math.floor((i * 4) / n));
  return { pitch, gap, kw, kh, keyX, group };
}

/** A knob: skirt + cap + pointer (pointing up). */
export function drawKnob(g: Graphics, x: number, y: number, r: number, cap: number, ow: number, angle = -0.6): void {
  g.circle(x, y, r)
    .fill({ color: 0x2b2540 })
    .stroke({ width: ow * 0.8, color: INK });
  g.circle(x, y, r * 0.68).fill({ color: cap });
  g.moveTo(x, y)
    .lineTo(x + Math.sin(angle) * r * 0.85, y - Math.cos(angle) * r * 0.85)
    .stroke({ width: Math.max(1, r * 0.22), color: 0xffffff, cap: "round" });
}

/** A snapping claw limb (arm + moving jaw + palm with fixed finger). Rest angle 0 = up. */
export function snapClaw(
  front: Anatomy["front"],
  limbs: Anatomy["limbs"],
  drawers: Anatomy["drawers"],
  gn: CreatureGenome,
  s: -1 | 1,
  x: number,
  y: number,
  H: number,
  ow: number,
  cs: number,
  armColor?: (c: Colors) => number,
): void {
  const claw = limb(front, x, y, "claw", s, 0, 0);
  const jaw = new Graphics();
  const palmX = s * H * 0.16,
    palmY = -H * 0.2,
    pr = H * 0.1 * cs;
  jaw.position.set(palmX - s * H * 0.035 * cs, palmY - pr * 0.55);
  claw.l.node.addChildAt(jaw, 1);
  const palm = new Graphics();
  claw.l.node.addChild(palm);
  claw.l.jaw = jaw;
  limbs.push(claw.l);
  drawers.push((c) => {
    const col = armColor ? armColor(c) : c.base;
    const arm = claw.g;
    arm.clear();
    noodle(arm, [0, 0, s * H * 0.08, -H * 0.06, palmX, palmY], H * 0.06, col, ow);
    jaw.clear();
    oval(jaw, 0, -H * 0.08 * cs, H * 0.042 * cs, H * 0.095 * cs, col, ow);
    palm.clear();
    oval(palm, palmX + s * H * 0.045 * cs, palmY - pr * 0.95, H * 0.045 * cs, H * 0.1 * cs, col, ow);
    disc(palm, palmX, palmY, pr, col, ow);
    blot(palm, palmX + s * pr * 0.25, palmY + pr * 0.2, pr * 0.45, pr * 0.35, gn.markings.multi ? pal(c, 0) : c.belly);
  });
}

/** Thin orange bird legs with three toes, static (move with the rig). */
export function birdLegs(
  back: Anatomy["back"],
  limbs: Anatomy["limbs"],
  drawers: Anatomy["drawers"],
  dx: number,
  topY: number,
  H: number,
  ow: number,
  col = 0xd55e00,
): void {
  const legs = limb(back, 0, 0, "leg", 0, 0, 0);
  limbs.push(legs.l);
  drawers.push(() => {
    const g = legs.g;
    g.clear();
    for (const s of sides) {
      const x = s * dx;
      noodle(g, [x, topY, x, -H * 0.015], H * 0.03, col, ow * 0.7);
      noodle(g, [x - H * 0.06, 0, x, -H * 0.02, x + H * 0.06, 0], H * 0.025, col, ow * 0.6);
    }
  });
}
